import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createEditor,
  Descendant,
  Path,
  Range,
  Node,
  Text,
  Editor,
  BaseRange,
} from "slate";
import { Slate, Editable, withReact } from "slate-react";
import { withHistory } from "slate-history";
import { Element } from "./components/Element";
import { Leaf } from "./components/Leaf";
import { Toolbar } from "./components/toolbar/Toolbar";
import { withLayout } from "./plugins/withLayout";
import { ElementType, CustomElement } from "./types";
import { ParagraphIndicator } from "./components/ParagraphIndicator";
import { useToast } from "@packages/contexts/ToastContext";
import { Button } from "@core/button";
import { Save } from "lucide-react";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AIContentRefContext } from "@packages/contexts/AIContentRefContext";
import { ExportAllToPDFButton } from "./components/export-all";

interface TextEditorProps {
  value: Descendant[];
  onChange: (value: Descendant[]) => void;
  onSave: (value: Descendant[]) => void;
  placeholder?: string;
  properties?: {
    title?: string;
    author?: string;
    imageUrl?: string;
    createdAt?: string;
  };
}

const defaultValue: Descendant[] = [
  {
    type: "paragraph" as ElementType,
    children: [{ text: "" }],
  } as CustomElement,
];

export const TextEditor = ({
  value = defaultValue,
  properties,
  onChange,
  onSave,
  placeholder = "articleGenerator.placeholder",
}: TextEditorProps) => {
  const editor = useMemo(
    () => withLayout(withHistory(withReact(createEditor()))),
    [],
  );
  const { t } = useTranslation();
  const editorRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [loadingRange, setLoadingRange] = useState<Range | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const lastSavedContent = useRef<string>(JSON.stringify(value));
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const save = useCallback(async () => {
    if (onSave) {
      console.log("Saving...");
      setIsSaving(true);
      const content = JSON.parse(JSON.stringify(editor.children));
      await onSave(content);
      lastSavedContent.current = JSON.stringify(editor.children);
      console.log("Saved!");
      setIsSaving(false);
    }
  }, [editor, onSave]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "s") {
        event.preventDefault();
        save();
      }
    },
    [save],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const currentContent = JSON.stringify(editor.children);
      const isContentEmpty =
        editor.children.length === 0 ||
        editor.children.every((node) => Node.string(node).trim().length === 0);
      if (currentContent !== lastSavedContent.current && !isContentEmpty) {
        save();
      }
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [editor, save]);

  const decorate = useCallback(
    ([node, path]: [Node, Path]): (BaseRange & { loading: boolean })[] => {
      const ranges: (BaseRange & { loading: boolean })[] = [];

      if (loadingRange && Text.isText(node)) {
        const nodeRange = Editor.range(editor, path);
        const intersection = Range.intersection(loadingRange, nodeRange);
        if (intersection) {
          ranges.push({
            ...intersection,
            loading: true,
          });
        }
      }

      return ranges;
    },
    [loadingRange, editor],
  );

  const renderElement = useCallback(
    (props: any) => {
      return <Element {...props} />;
    },
    [editor],
  );

  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);

  const aiContentNodeRefsRef = useRef<Set<HTMLDivElement>>(new Set());

  const registerRef = (ref: HTMLDivElement) => {
    aiContentNodeRefsRef.current.add(ref);
  };

  const unregisterRef = (ref: HTMLDivElement) => {
    aiContentNodeRefsRef.current.delete(ref);
  };
  console.log(aiContentNodeRefsRef.current);

  // console.log(
  //   getComputedStyle(document.documentElement).getPropertyValue(
  //     "--arc-palette-title",
  //   )
  //     ? "Is Arc"
  //     : "Is Not Arc",
  // );

  return (
    <AIContentRefContext.Provider value={{ registerRef, unregisterRef }}>
      <div className="w-full border border-gray-200 dark:border-gray-700 rounded-lg relative">
        <div className="absolute -top-4 -left-4 z-50">
          <Button
            onClick={async () => {
              const currentContent = JSON.stringify(editor.children);
              const isContentEmpty =
                editor.children.length === 0 ||
                editor.children.every(
                  (node) => Node.string(node).trim().length === 0,
                );
              if (
                currentContent !== lastSavedContent.current &&
                !isContentEmpty
              ) {
                await save();
              }
              navigate(-1);
            }}
            size="lg"
            rounded="full"
            bgColor="bg-gray-200 hover:bg-gray-300 focus:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600"
            textColor="text-gray-800 dark:text-gray-200"
            className="shadow-xl px-3 py-1 flex items-center gap-2 transition-transform duration-150 transform hover:scale-105 focus:scale-105 focus:ring-2 focus:ring-gray-400/60 focus:ring-offset-2"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold text-base">{t("common.back")}</span>
          </Button>
        </div>
        <Slate editor={editor} initialValue={value} onChange={onChange}>
          <Toolbar />
          <div className="p-4 relative">
            <div className="relative" ref={editorRef}>
              {mounted && (
                <ParagraphIndicator
                  editor={editor}
                  elementRef={editorRef.current}
                  setLoadingRange={setLoadingRange}
                  saveArticle={save}
                />
              )}
              <Editable
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder={placeholder}
                spellCheck
                className="min-h-[200px] focus:outline-none pl-10"
                onKeyDown={handleKeyDown}
                decorate={decorate}
              />
            </div>
          </div>
        </Slate>
        <div className="fixed flex flex-row gap-2 bottom-6 right-6 z-50">
          <ExportAllToPDFButton
            getRefs={() => Array.from(aiContentNodeRefsRef.current)}
            slateContent={editor.children}
            properties={properties}
          />

          <Button
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await save();
            }}
            size="lg"
            rounded="full"
            bgColor="bg-green-500 hover:bg-green-600 focus:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
            textColor="text-white"
            className={`shadow-xl px-6 py-3 flex items-center gap-2 transition-transform duration-150 transform hover:scale-105 focus:scale-105 focus:ring-2 focus:ring-green-400/60 focus:ring-offset-2 ${isSaving ? "opacity-70 pointer-events-none cursor-wait" : ""}`}
            aria-label="Save changes"
            disabled={isSaving}
          >
            <Save className="w-5 h-5" />
            <span className="font-semibold text-base">
              {isSaving ? t("common.saving") : t("articleGenerator.saveButton")}
            </span>
          </Button>
        </div>
      </div>
    </AIContentRefContext.Provider>
  );
};
